import React, { memo, useCallback, useContext, useMemo } from "react";
import { FreebieWidgetRenderingDetails } from "@swiggy-private/cart-types";
import { FreebieWidget } from "@swiggy-private/cart-ui";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";
import { SnaccTheme } from "@swiggy-private/dls-web-common/dist/components/themes/Snacc";
import { isEmpty } from "@swiggy-private/js-utils";
import { ErrorBoundary } from "@swiggy-private/core-ui";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartToastContext } from "@cart-client/contexts/CartToastContext";
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import { useCartRendererAnalytics } from "@cart-client/hooks/useCartRenderedAnalytics";
import { isMerchItem, getItemType } from "@swiggy-private/cart-ui";
import { useCartRendererXP } from "@cart-client/hooks/useCartRendererXP";

interface FreebieWidgetRendererProps {
    widgetData: FreebieWidgetRenderingDetails;
    cartResponse: ReviewedCart;
}

const FreebieWidgetRenderer: React.FC<FreebieWidgetRendererProps> = ({
    widgetData,
    cartResponse,
}: FreebieWidgetRendererProps) => {
    const { onImpressionFreebieWidget, onClickFreebieClaimButton } = useCartRendererAnalytics(cartResponse);
    const { doUpdateCart, setShowFreebiePopup } = useContext(UpdateCartContext);
    const {
        cartState: { localCart },
    } = useContext(CartContext);
    const { setCartToastData } = useContext(CartToastContext);

    const { cartId, originalCartId } = cartResponse?.data || {};
    const itemId = String(widgetData?.meta?.items[0]?.menu_item_id || "");
    const item = widgetData?.meta?.items[0];
    const isMerchItemFlag = useMemo(() => isMerchItem(item || {}), [item]);
    const itemType = useMemo(() => getItemType(item || {}), [item]);
    const { isSnaccStorefrontV2 } = useCartRendererXP(cartResponse);

    const handleClaim = useCallback(async () => {
        const isUnlocked = widgetData?.meta?.movMet || false;
        if (!isUnlocked) return;
        onClickFreebieClaimButton?.(itemId, itemType);
        try {
            const response = await doUpdateCart({
                ...localCart,
                cartItems: localCart?.cartItems?.concat([
                    {
                        ...widgetData?.meta?.items[0],
                        quantity: 1,
                    },
                ]),
            });

            // Show popup after successful API call only for non-merch items
            if (response?.data && !isMerchItemFlag) {
                setShowFreebiePopup?.(true);
            }
        } catch (error) {
            setCartToastData({
                message: "Failed to claim freebie. Please try again.",
                show: true,
            });
        }
    }, [
        widgetData?.meta?.movMet,
        widgetData?.meta?.items,
        onClickFreebieClaimButton,
        itemId,
        doUpdateCart,
        localCart,
        setShowFreebiePopup,
        setCartToastData,
        isMerchItemFlag,
        itemType,
    ]);

    const onImpression = useCallback(() => {
        onImpressionFreebieWidget({
            cartId: String(cartId),
            originalCartId: String(originalCartId),
            itemId,
            freebieUnlocked: widgetData?.meta?.movMet ? "true" : "false",
            itemType,
        });
    }, [onImpressionFreebieWidget, cartId, originalCartId, itemId, widgetData?.meta?.movMet, itemType]);
    if (isEmpty(widgetData?.meta)) return null;
    return (
        <ErrorBoundary errorContent={null}>
            <ThemeProvider theme={SnaccTheme} colorMode="light">
                <FreebieWidget widgetData={widgetData?.meta} onClaim={handleClaim} onImpression={onImpression} isSnaccStorefrontV2={isSnaccStorefrontV2} />
            </ThemeProvider>
        </ErrorBoundary>
    );
};

export default memo(FreebieWidgetRenderer);
